import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useSyncedTransactionsSearch, useSyncedTransactionActivity, useSyncedTransactionTypeHierarchy } from '../../../../../api/coreData'
import useColumnConfig from '../../../QueryBlueprint/hooks/useColumnConfig'
import { resolveQuickDate } from './quickDates'
import { DEFAULT_PAGE_SIZE, EMPTY_HIERARCHY_QUERY } from './constants'
import { ALL_AVAILABLE_COLUMNS, buildDefaultColumns } from './columnDefs'
import {
  DEFAULT_INCLUDES,
  mergeConfig,
  buildActivitySelect,
  parseActivityResult,
  buildScope,
  buildFilters,
  buildTextSearch,
  mapSortState
} from './queryBuilders'

const SyncedTransactionsContext = createContext(null)

export function useSyncedTransactions () {
  const ctx = useContext(SyncedTransactionsContext)
  if (!ctx) throw new Error('useSyncedTransactions must be used within SyncedTransactionsProvider')
  return ctx
}

export default function SyncedTransactionsProvider ({ config: rawConfig, children }) {
  const merged = useMemo(() => mergeConfig(rawConfig), [rawConfig])
  const { clientId } = useAppContext()
  const { data: hierarchyResult } = useSyncedTransactionTypeHierarchy(EMPTY_HIERARCHY_QUERY)
  const hierarchy = hierarchyResult?.data ?? null

  const [activeMetricGroupId, setActiveMetricGroupId] = useState(null)

  const [quickDate, setQuickDate] = useState(merged.defaultQuickDate)
  const [customDateRange, setCustomDateRange] = useState(null)
  const [filterState, setFilterState] = useState({
    transactionTypes: [],
    iscustodialcash: '',
    transamountMin: null,
    transamountMax: null
  })
  const [searchText, setSearchText] = useState('')
  const [sortState, setSortState] = useState(
    merged.defaultSort
      ? [{ id: merged.defaultSort.field, desc: merged.defaultSort.dir === 'desc' }]
      : []
  )
  const [pageState, setPageState] = useState({
    pageIndex: 0,
    pageSize: merged.defaultPageSize ?? DEFAULT_PAGE_SIZE
  })

  const dateRange = useMemo(() => {
    if (quickDate === 'Custom' && customDateRange) return customDateRange
    return resolveQuickDate(quickDate) ?? resolveQuickDate('YTD')
  }, [quickDate, customDateRange])

  const columns = useMemo(() => {
    const base = merged.columns ?? buildDefaultColumns()
    const catalogMap = new Map(ALL_AVAILABLE_COLUMNS.map(c => [c.name, c]))
    return base.map(col => {
      const catalog = catalogMap.get(col.name)
      if (!catalog) return col
      return {
        truncate: catalog.truncate,
        accessorKey: catalog.accessorKey,
        ...col
      }
    })
  }, [merged.columns])

  const storageKey = merged.localStorageKey || null
  const { mergedColumns, handleColumnConfigSave } = useColumnConfig(columns, storageKey)

  const activeMetricGroup = useMemo(() => {
    if (!activeMetricGroupId || !merged.metricGroups?.length) return null
    return merged.metricGroups.find(g => g.id === activeMetricGroupId) ?? null
  }, [activeMetricGroupId, merged.metricGroups])

  const activitySelect = useMemo(
    () => buildActivitySelect(merged.metricGroups),
    [merged.metricGroups]
  )

  const activityQuery = useMemo(() => {
    if (!activitySelect || !clientId) return null
    return {
      levelIds: [clientId],
      groupings: [{ type: 'client' }],
      dateRange,
      select: activitySelect
    }
  }, [activitySelect, clientId, dateRange])

  const activityEnabled = !!merged.showMetrics && !!activityQuery && !!clientId
  const { data: activityResult, isFetching: isFetchingActivity } = useSyncedTransactionActivity(
    activityQuery,
    { enabled: activityEnabled }
  )

  const activityData = useMemo(() => {
    const rows = activityResult?.data
    if (!rows?.length) return null
    return parseActivityResult(rows[0], merged.metricGroups)
  }, [activityResult, merged.metricGroups])

  const includes = merged.includes ?? DEFAULT_INCLUDES

  const query = useMemo(() => {
    const scope = buildScope(clientId)
    return {
      scope,
      dateRange,
      filters: buildFilters(filterState, hierarchy, activeMetricGroup),
      textSearch: buildTextSearch(searchText),
      sort: mapSortState(sortState),
      includes,
      skip: pageState.pageIndex * pageState.pageSize,
      take: pageState.pageSize
    }
  }, [clientId, dateRange, filterState, hierarchy, activeMetricGroup, searchText, sortState, includes, pageState])

  const hasScope = query.scope?.length > 0 && query.scope[0]?.ids?.length > 0
  const { data: result, isFetching } = useSyncedTransactionsSearch(query, {
    enabled: hasScope && !!dateRange
  })

  const rows = useMemo(() => result?.data ?? [], [result?.data])
  const total = result?.total ?? 0

  const resetPage = useCallback(() => {
    setPageState(prev => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleQuickDateChange = useCallback((token) => {
    setQuickDate(token)
    if (token === 'Custom') {
      setCustomDateRange({
        startDate: dayjs().subtract(31, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD')
      })
    } else {
      setCustomDateRange(null)
    }
    resetPage()
  }, [resetPage])

  const handleCustomDateRangeChange = useCallback((range) => {
    setCustomDateRange(range)
    setQuickDate('Custom')
    resetPage()
  }, [resetPage])

  const handleFilterChange = useCallback((patch, replace) => {
    if (replace) {
      setFilterState(patch)
    } else {
      setFilterState(prev => ({ ...prev, ...patch }))
    }
    resetPage()
  }, [resetPage])

  const handleSearchChange = useCallback((text) => {
    setSearchText(text)
    resetPage()
  }, [resetPage])

  const handleSortChange = useCallback((updater) => {
    setSortState(typeof updater === 'function' ? updater : () => updater)
  }, [])

  const handlePageChange = useCallback((updater) => {
    setPageState(typeof updater === 'function' ? updater : () => updater)
  }, [])

  const handleMetricGroupToggle = useCallback((groupId) => {
    setActiveMetricGroupId(prev => prev === groupId ? null : groupId)
    resetPage()
  }, [resetPage])

  const ctx = useMemo(() => ({
    config: merged,
    quickDate,
    customDateRange,
    dateRange,
    filterState,
    searchText,
    sortState,
    pageState,
    columns,
    mergedColumns,
    handleColumnConfigSave,
    rows,
    total,
    isFetching,
    query,
    activityData,
    isFetchingActivity,
    activeMetricGroupId,
    onQuickDateChange: handleQuickDateChange,
    onCustomDateRangeChange: handleCustomDateRangeChange,
    onFilterChange: handleFilterChange,
    onSearchChange: handleSearchChange,
    onSortChange: handleSortChange,
    onPageChange: handlePageChange,
    onMetricGroupToggle: handleMetricGroupToggle
  }), [
    merged, quickDate, customDateRange, dateRange,
    filterState, searchText, sortState, pageState,
    columns, mergedColumns, handleColumnConfigSave,
    rows, total, isFetching, query,
    activityData, isFetchingActivity, activeMetricGroupId,
    handleQuickDateChange, handleCustomDateRangeChange,
    handleFilterChange, handleSearchChange,
    handleSortChange, handlePageChange, handleMetricGroupToggle
  ])

  return (
    <SyncedTransactionsContext.Provider value={ctx}>
      {children}
    </SyncedTransactionsContext.Provider>
  )
}

SyncedTransactionsProvider.propTypes = {
  config: PropTypes.object,
  children: PropTypes.node
}
